import dragula from "dragula";

import * as Popup from "../popup.js";

/**
 * Reusable function to create a specific kanban alpine data using common behavior for "free".
 *
 * ## Example
 *
 * ```js
 *  Alpine.data("crmKanban", (options) => ({
 *    ...Kanban(options),
 *    ...{
 *      cardPath(c) {
 *        return "";
 *      },
 *      handleLoadResponse(response) {
 *        this.cards = response;
 *      },
 *      cardsInColumn(columnId) {
 *        return this.cards.filter((c) => c.pipeline_state == columnId);
 *      },
 *      updateCardColumn(customerId, newState) {
 *        return API.fetchJSON(...);
 *      },
 *      batchMoveCards(cards, newState) {
 *        return API.fetchJSON(...);
 *      },
 *      batchDeleteCards(cards) {
 *        return API.fetchJSON(...);
 *      },
 *      storeCollapsedColumns() {
 *        // TODO
 *      },
 *      loadCollapsedColumns() {
 *        // TODO
 *      }
 *    }
 * }));
 * ```
 *
 * @param {*} options
 * @returns Object
 */
export const Kanban = (options) => ({
  drake: null,
  cards: [],
  selectedCards: [],
  filtersCache: false,
  collapsedColumns: [],
  dragAndDrop: options.dragAndDrop,
  includesFrozen: options.frozen,
  selectedCard: null,
  // Used to keep track of columns where items where dropped
  // Useful to hide no entity message after drop
  // This is needed because we optimistically change the state on the client side and do not reload all training sessions after drop
  droppedColumns: [],
  async init() {
    if (this.dragAndDrop) {
      const pipelineColumns = Array.from(this.$el.querySelectorAll("[data-column-id]"));

      this.drake = dragula(pipelineColumns, {
        moves: (el) => {
          // Drag&drop only makes sense if we have multiple columns
          return this.hasMultipleColumns() && el.closest("[data-movable]")?.dataset.movable;
        },
        copy: false,
      })
        .on("cloned", (mirror, original, type) => {
          // Disabling alpine execution on the mirror image dragula creates
          mirror.setAttribute("x-ignore", true);

          // The mirror lives in <body>, out of reach of the collapsed column styles that hide the card
          // body, so drop it by hand when the card is dragged from a collapsed column.
          if (type === "mirror" && original.closest(".kanban__column--collapsed")) {
            mirror.querySelector(".kanban__card__body")?.remove();
          }
        })
        .on("drop", (...args) => this.onDrop.apply(this, args));
    }

    document.addEventListener(`${options.name}:data`, (event) => {
      this.cards = []; // Reset cards, this gives alpine time to cleanup the state which can be messed-up by drag&dropping items
      this.selectedCards = [];
      this.droppedColumns = [];
      this.handleLoadResponse(event.detail.data);
      window.requestAnimationFrame(() => this.calcAndSetColumnsHeight());
    });

    document.addEventListener(`${options.name}:partialData`, (event) => {
      this.handlePartialLoadResponse(event.detail.data);
    });

    this.initDragScroll();
    this.loadCollapsedColumns(options);

    if (options.scroll) {
      this.calcAndSetHeight();
      this.ro = new ResizeObserver(() => this.calcAndSetHeight());
    } else {
      this.calcAndSetColumnsHeight();
      this.ro = new ResizeObserver(() => this.calcAndSetColumnsHeight());
    }
    this.ro.observe(this.$el);
  },
  // Set all columns to the same height so that droppable zones are as big as they can be
  calcAndSetColumnsHeight() {
    const columns = Array.from(this.$refs.kanban.querySelectorAll(".js-kanban-column-body"));
    // Reset to normal height for calculation, this step is essential
    columns.forEach((column) => column.style.removeProperty("min-height"));

    if (!this.hasMultipleColumns()) return;

    const maxHeight = Math.max(...columns.map((column) => column.offsetHeight));
    if (maxHeight > 0) {
      columns.forEach((column) => column.style.setProperty("min-height", `${maxHeight}px`));
    }
  },
  loadCollapsedColumns(options) {
    this.collapsedColumns = options.collapsedColumns;
  },
  initDragScroll() {
    let isDrag = false;

    const dragStart = (event) => {
      if (event.target != this.$refs.columns && !event.target.dataset.columnId) return;
      this.$el.classList.add("has-select-none");
      isDrag = true;
    };
    const dragEnd = () => {
      this.$el.classList.remove("has-select-none");
      isDrag = false;
    };
    const drag = (ev) => {
      return isDrag && (this.$refs.kanban.scrollLeft -= ev.movementX);
    };

    this.$refs.columns.addEventListener("pointerdown", dragStart);
    this.$refs.columns.addEventListener("pointerup", dragEnd);
    this.$refs.columns.addEventListener("pointermove", drag);
  },
  onDrop(el, target, source) {
    // Be warned : there is something weird going on here
    // When drag&dropping cards we are not updating our local alpine state
    // This is a wanted behaviour
    // If we were to update it we would end up with the dropped card moving from where it was dropped when updating alpine state which is is visually disturbing
    // Only when calling `load` function (called for example when changing filters) the state will be "cleaned" and order corrected but at this point all cards are reload and it's not visually disturbing

    const newState = target?.dataset?.columnId;
    const previousState = source?.dataset?.columnId;

    if (!newState || !previousState || newState == previousState) return this.drake.cancel(true);

    const cardId = el.dataset.id;
    const card = this.cards.find((c) => c.id == cardId);

    if (!card) {
      console.error(`Could not find card with id ${cardId}`);
      return this.drake.cancel(true);
    }

    this.droppedColumns.push(newState);

    this.updateCardColumn(cardId, newState)
      .then(() => window.requestAnimationFrame(() => this.calcAndSetColumnsHeight()))
      .catch((...args) => this.handleError.apply(this, args));
  },
  onToggleCollapse(state) {
    if (this.collapsedColumns.includes(state)) {
      this.collapsedColumns = this.collapsedColumns.filter((s) => s != state);
    } else {
      this.collapsedColumns.push(state);
    }

    this.storeCollapsedColumns();
  },
  onBatchMove(newState) {
    this.open = false;
    this.loading = true;

    this.batchMoveCards(this.selectedCards, newState).catch((...args) => this.handleError.apply(this, args));
  },
  onBatchDelete() {
    Popup.close(this.$refs.deleteConfirmPopup);

    this.open = false;
    this.loading = true;

    this.batchDeleteCards(this.selectedCards).catch((...args) => this.handleError.apply(this, args));
  },
  selectCard(card) {
    this.selectedCard = card.id;

    this.$petrobar.open();
    this.$dispatch("kanban-selected-card", {
      type: this.name,
      args: {
        card: card,
      },
    });
  },
  unselectCard(event) {
    if (event.target.closest(".app-page__petrobar") || event.target.closest(".app-nav-mobile")) return;

    if (!this.selectedCard) return;
    this.selectedCard = null;
    this.$dispatch("kanban-selected-card", {
      type: this.name,
      args: {
        card: null,
      },
    });
  },
  handleError(error) {
    if (error == AbortedDoubleRequest) {
      return;
    }
    console.error(error);
    this.$toast.show("#kanban-error-toast");
    this.reloadData?.();
  },
  hasMultipleColumns() {
    const computedStyles = window.getComputedStyle(this.$refs.columns);
    return computedStyles.getPropertyValue("flex-direction") == "row";
  },
});

class AbortedDoubleRequest extends Error {
  constructor(message) {
    super(message);
    this.name =
      "Used as error when aborting a request to avoid double request to the same resource simultaneously. This error should be filtered out.";
  }
}
